import {
  MKTX_DEALER_LIST_MODES,
  MKTX_INQUIRY_TYPES,
  defaultListName,
  ORDER_SIDES,
  PROTOCOL_TYPES,
  TRUMID_PARTIAL_EXECUTION,
  TRUMID_PRICE_TYPES,
  TRUMID_RFQ_INQUIRY_TYPES,
  TRUMID_STIPULATION_TYPES,
} from '../../Constants/AppConstants';
import { createEmptyRow } from '../../Store/listRfqStore';
import { isListNameEditEligible } from '../../Utils/listRfqStatus';
import { normalizeYesNoToYN } from './useOrderEntry';

/**
 * Turning a re-initiated List RFQ order back into what the List Order tab holds.
 *
 * In plain terms: the trader clicked Re-Initiate on one bond of a list, and this works out
 * what the List Order tab should already be showing when they open it - the enquiry settings
 * and one grid row per bond of that list.
 *
 * Kept as plain functions with no React and no store writes, for the same reason
 * listRfqPayload.js is: that file decides what goes ON the wire, this one decides what comes
 * back OFF it, and both can be read on their own. The hook in useListRfqPrefill.js does the
 * fetching and the store writes.
 *
 * <p><b>Where each value comes from.</b> Two sources:
 * <ul>
 *   <li>GET /order/list/{id}/items (ListRfqItemDTO) - every bond of the list, with its own
 *       cusip, side, quantity, price, price type, stipulation, partial-fill flag and dealers.
 *       This is the source for the grid rows.</li>
 *   <li>The blotter row the trader clicked (ExtOrderDTO) - the enquiry-level settings
 *       (account, inquiry type, deadline, quote firm time), which are one per list rather
 *       than one per bond.</li>
 * </ul>
 *
 * <p>The per-bond settings used to be missing from the endpoint, so only the clicked bond's row
 * could be filled and every other row came up on blank-row defaults. The endpoint now returns
 * them per bond, which is what lets a whole list come back as it was raised. The blotter row is
 * kept as a fallback for its own bond alone, so a frontend deployed ahead of the backend still
 * behaves as it did rather than losing what it used to recover.
 *
 * <p>Nothing is ever guessed from a neighbouring bond. Dealers and price type are trading
 * decisions; copying one bond's onto another would put a choice in front of the trader that
 * nobody made. A bond whose value we genuinely do not have keeps the blank-row default and is
 * flagged by the grid's own per-row validation before anything can be sent.
 */

/**
 * Is this order one bond of a list that we are able to prefill from?
 *
 * A listId or venue list marker establishes association, and orderId is what
 * GET /order/list/{id}/items is addressed by.
 */
export const isListPrefillCandidate = (order) =>
  !!order?.orderId && (
    (order.listId !== null && order.listId !== undefined) ||
    (order.protocol === PROTOCOL_TYPES.MARKETAXESS && !!order.mktxListId) ||
    (order.protocol === PROTOCOL_TYPES.TRUMID_RFQ && !!order.tradingSessionSubId)
  );

/**
 * The enquiry-level settings of the list, read off the one order the trader re-initiated.
 *
 * Reading them off a single bond is exact rather than a guess: the backend requires
 * rfqInquiryType <28006>, ttl <28019> and quoteSubmissionDeadline <28020> to be IDENTICAL on
 * every bond of a list (QuoteRequestServiceTrumid.createListRfqDto -> requireSameListLevelValue)
 * and refuses a list whose orders sit on different accounts (handleTrumidListProtocol), so any
 * one bond carries the whole list's values.
 *
 * Units are already right, and that was checked rather than assumed: the blotter projection
 * selects rm.dueInTimeMinutes and rm.goodForTimeMinutes (OrdersRepository.findOrdersByCustId /
 * findOrdersByEntryBy), which OrderMapper puts on quoteSubmissionDeadline and ttl - MINUTES,
 * which is what both fields on this tab are labelled and validated in. No conversion.
 *
 * Every value is range-checked before it is offered, so a row carrying something the tab could
 * not validate leaves that field on its default instead of opening the tab already in error.
 * Trumid keeps the store's default protocol. MarketAxess explicitly restores its protocol
 * for both the read-only list view and whole-list re-initiate.
 *
 * @param   {object} order the re-initiated blotter row (ExtOrderDTO)
 * @returns {object} a patch for listRfqData - only the fields we could recover
 */
const savedDealerSet = (venue) => [...new Set(
  (Array.isArray(venue) ? venue : String(venue || '').split(','))
    .map((dealer) => String(dealer).trim()).filter(Boolean)
)].sort();

// Display inference agreed with the user: identical nonempty sets mean list-wide.
// Identical independently selected item dealers are intentionally displayed list-wide too.
export const inferMktxDealerTargeting = (items = []) => {
  const sets = (Array.isArray(items) ? items : []).map((item) => savedDealerSet(item?.venue));
  const first = sets[0] || [];
  const listWide = first.length > 0 && sets.every((set) =>
    JSON.stringify(set) === JSON.stringify(first));
  return { mktxDealerScope: listWide ? 'LIST' : 'ITEM', mktxVenue: listWide ? first : [] };
};

export const buildPrefillSharedFields = (order = {}, items = []) => {
  const fields = {};

  if (order.account) fields.account = order.account;
  if (order.protocol === PROTOCOL_TYPES.MARKETAXESS) {
    fields.protocol = PROTOCOL_TYPES.MARKETAXESS;
    fields.rfqInquiryType = MKTX_INQUIRY_TYPES.BIN;
    if (order.mktxPricingProcess) fields.mktxPricingProcess = order.mktxPricingProcess;
    if (Object.values(MKTX_DEALER_LIST_MODES).includes(order.mktxDealerListMode)) {
      fields.mktxDealerListMode = order.mktxDealerListMode;
    }
    Object.assign(fields, inferMktxDealerTargeting(items));
    if (order.comments) fields.comments = order.comments;
    fields.listName = defaultListName(order.protocol);
    return fields;
  }

  fields.protocol = PROTOCOL_TYPES.TRUMID_RFQ;
  const inquiryType = String(order.rfqInquiryType || '').trim().toUpperCase();
  if (
    inquiryType === TRUMID_RFQ_INQUIRY_TYPES.ASAP ||
    inquiryType === TRUMID_RFQ_INQUIRY_TYPES.BIN
  ) {
    fields.rfqInquiryType = inquiryType;
  }

  // 2-30 is the same window validateListRfq enforces (listRfqPayload.js), so anything outside
  // it would land the trader on a red field they never typed.
  const deadline = Number(order.quoteSubmissionDeadline);
  if (Number.isInteger(deadline) && deadline >= 2 && deadline <= 30) {
    fields.quoteSubmissionDeadline = deadline;
  }

  // Quote firm time is BIN-only (Trumid RFQ spec p.46), so it is offered only when the
  // recovered inquiry type is BIN - the same condition the tab shows the field under.
  const ttl = Number(order.ttl);
  if (
    fields.rfqInquiryType === TRUMID_RFQ_INQUIRY_TYPES.BIN &&
    Number.isInteger(ttl) &&
    ttl >= 2 &&
    ttl <= 30
  ) {
    fields.ttl = ttl;
  }

  if (order.comments) fields.comments = order.comments;
  // A re-initiated list is a new list, so it starts on the default name rather than the old
  // list's; the trader can change it. The read-only view sets the real name itself
  // (buildViewSharedFields below).
  fields.listName = defaultListName(order.protocol || PROTOCOL_TYPES.TRUMID_RFQ);

  return fields;
};

/**
 * The enquiry-level settings for the saved list view (List ID / List Name click):
 * the same as a re-initiate, except the name shown is the list's own.
 */
export const buildViewSharedFields = (order = {}, items = []) => {
  // The fetched list is newer than the clicked blotter row, including a name cleared elsewhere.
  const name = items[0]?.listName !== undefined ? items[0].listName : order.listName;
  // Finished siblings do not lock the name while another RFQ is still live.
  const hasEligibleRfq = items.some((item) => isListNameEditEligible(item.status));
  return { ...buildPrefillSharedFields(order, items), listName: name || '', savedListName: name || '',
    canEditListName: items[0]?.canEditListName === true && hasEligibleRfq };
};

/**
 * The per-bond settings of the ONE bond the trader re-initiated, as grid-row fields.
 *
 * Read off the blotter row (ExtOrderDTO). Since ListRfqItemDTO began returning these four
 * per bond, this is no longer the only source - see buildPrefillRowSettingsFromItem below,
 * which is preferred because it answers for EVERY bond rather than just the clicked one. This
 * remains as the fallback for that one bond, so a frontend deployed ahead of the backend still
 * recovers what it always did instead of losing it.
 *
 * Mirrors what the single-bond ticket does with the same row: Pages/Order.jsx
 * handleResubmitRequest hands the row straight to the Bond Order tab, so the same four values
 * reappear there. The difference is the shape, not the meaning - venue is a comma-separated
 * string on the wire and an array in the grid, and allowPartialExecution comes back from
 * OrderMapper as "Yes"/"No" while the grid stores "Y"/"N".
 *
 * @param   {object} order the re-initiated blotter row (ExtOrderDTO)
 * @returns {object} a patch for one grid row - only the fields we could recover
 */
export const buildPrefillRowSettings = (order = {}) => {
  const settings = {};

  const priceType = String(order.priceType || '').trim().toUpperCase();
  if (priceType === TRUMID_PRICE_TYPES.PRICE || priceType === TRUMID_PRICE_TYPES.SPREAD) {
    settings.priceType = priceType;
  }

  // Stipulations are "Conditionally required when PriceType=6" (spec p.50) and meaningless
  // otherwise, so one is carried over only onto a SPREAD bond. withStipulationRule in the
  // store enforces the same pairing whenever the trader changes the price type afterwards.
  const stipulationType = String(order.stipulationType || '').trim().toUpperCase();
  if (
    settings.priceType === TRUMID_PRICE_TYPES.SPREAD &&
    (stipulationType === TRUMID_STIPULATION_TYPES.SPOT ||
      stipulationType === TRUMID_STIPULATION_TYPES.HEDGE)
  ) {
    settings.stipulationType = stipulationType;
  }

  // normalizeYesNoToYN is imported from useOrderEntry rather than rewritten: it is already
  // exported and already used for this exact conversion by TruMidRFQFields.jsx (line 11), and
  // the "Yes"/"No" spelling it undoes is produced by the backend's OrderMapper.
  const allowPartialExecution = normalizeYesNoToYN(order.allowPartialExecution);
  if (
    allowPartialExecution === TRUMID_PARTIAL_EXECUTION.YES ||
    allowPartialExecution === TRUMID_PARTIAL_EXECUTION.NO
  ) {
    settings.allowPartialExecution = allowPartialExecution;
  }

  // Normally already an array by the time it reaches here - normalizeVenueValue in
  // Pages/Order.jsx (~line 51) splits it for every MULTI_VENUE_PROTOCOLS protocol, and Trumid
  // RFQ is one of them - but the raw comma-separated string is handled too so this function
  // does not depend on having been called downstream of that one.
  const venue = Array.isArray(order.venue)
    ? order.venue
    : String(order.venue || '').split(',');
  const dealers = venue.map((dealer) => String(dealer).trim()).filter(Boolean);
  if (dealers.length) settings.venue = dealers;

  return settings;
};

/**
 * The per-bond settings of ANY bond of the list, taken from that bond's own item.
 *
 * In plain terms: how this bond was priced, what stipulation it carried, whether it allowed
 * partial fills, and which dealers it went to - for each bond in turn, not just the one the
 * trader clicked.
 *
 * Every value is used as the endpoint sends it, because the backend already sends the grid's
 * own vocabulary: rfq_master stores price type as "PRICE"/"SPREAD" and stipulation as
 * "SPOT"/"HEDGE", and getListRfqItems converts allow_partial_fills from its stored 1/0 to the
 * "Y"/"N" the grid and spec p.48 both use. Only dealers need reshaping - they arrive as the one
 * comma-separated string order_mst.venue holds and the grid wants an array.
 *
 * Each field is written only when it is recognised, so createEmptyRow's default survives
 * anything unexpected instead of a bad value reaching the payload. A field the endpoint omits -
 * an older backend - simply leaves the default in place.
 *
 * <b>Differs from buildPrefillRowSettings</b> only in where it reads from: that one takes a
 * blotter row, whose allowPartialExecution is "Yes"/"No" from OrderMapper and needs
 * normalising, and whose venue may already be an array. Same four fields, same output shape.
 *
 * @param   {object} item one ListRfqItemDTO
 * @returns {object} a patch for that bond's grid row - only the fields we could recover
 */
export const buildPrefillRowSettingsFromItem = (item = {}) => {
  const settings = {};

  const priceType = String(item.priceType || '').trim().toUpperCase();
  if (priceType === TRUMID_PRICE_TYPES.PRICE || priceType === TRUMID_PRICE_TYPES.SPREAD) {
    settings.priceType = priceType;
  }

  // Price type and stipulation are ONE decision, so this answers for both or for neither.
  //
  // Writing stipulationType only when there is one to write would let a recognised PRICE bond
  // merge over a SPREAD fallback and leave PRICE carrying that fallback's stipulation - an
  // invalid pair, which spec p.50 forbids ("Conditionally required when PriceType=6") and the
  // store's withStipulationRule would have to undo. So a recognised price type always sets the
  // stipulation too, to null on a PRICE bond.
  //
  // When the price type is NOT recognised - an older backend sending neither - both are left
  // alone, so the blotter-row fallback still supplies the pair intact.
  if (settings.priceType) {
    const stipulationType = String(item.stipulationType || '').trim().toUpperCase();
    settings.stipulationType =
      settings.priceType === TRUMID_PRICE_TYPES.SPREAD &&
      (stipulationType === TRUMID_STIPULATION_TYPES.SPOT ||
        stipulationType === TRUMID_STIPULATION_TYPES.HEDGE)
        ? stipulationType
        : null;
  }

  // Already Y/N from the endpoint, so no normalising - unlike the blotter-row version above.
  const allowPartialExecution = String(item.allowPartialExecution || '').trim().toUpperCase();
  if (
    allowPartialExecution === TRUMID_PARTIAL_EXECUTION.YES ||
    allowPartialExecution === TRUMID_PARTIAL_EXECUTION.NO
  ) {
    settings.allowPartialExecution = allowPartialExecution;
  }

  const dealers = String(item.venue || '')
    .split(',')
    .map((dealer) => dealer.trim())
    .filter(Boolean);
  if (dealers.length) settings.venue = dealers;

  return settings;
};

/**
 * One grid row per bond of the list.
 *
 * Every bond the endpoint returns gets a row, with no filtering on status. That is deliberate:
 * a list expires as a whole (one deadline for the enquiry), so the siblings of an expired bond
 * are normally expired too, and dropping rows the trader cannot see a reason for would be
 * worse than showing them - somebody who knows the list held ten bonds and is shown eight has
 * no way to find out why. The grid is a draft that is reviewed and sent by hand, and any row
 * can be removed.
 *
 * Rows are built through createEmptyRow so every field the grid needs exists and carries the
 * store's own default when we could not recover it - including a fresh rowId, which the whole
 * grid is keyed by.
 *
 * Every bond carries its own price type, stipulation, partial-fill setting and dealers, taken
 * from its own item. The clicked bond additionally falls back to the blotter row for anything
 * its item did not carry, which matters only against a backend older than those fields: the
 * item wins wherever it has an answer, so the two can never disagree.
 *
 * The clicked bond's settings are NOT copied onto the other rows. They never were, and now
 * there is no reason to - each bond answers for itself. Spreading one bond's dealers across a
 * list would put a trading decision on screen that nobody made.
 *
 * @param   {Array}  items       ListRfqItemDTO[] from GET /order/list/{id}/items
 * @param   {object} sourceOrder the re-initiated blotter row, used only as a fallback for its own bond
 * @param   {object} options reinitiate uses fresh MA times; omitted for historical read-only views
 * @returns {Array}  rows for the store, in the order the endpoint returned them
 */
export const buildPrefillRows = (items = [], sourceOrder = {}, { reinitiate = false } = {}) => {
  const sourceOrderId = sourceOrder?.orderId;
  const rowSettings = buildPrefillRowSettings(sourceOrder);
  const isMarketAxess = sourceOrder?.protocol === PROTOCOL_TYPES.MARKETAXESS;
  const dealersPerBond = isMarketAxess && inferMktxDealerTargeting(items).mktxDealerScope === 'ITEM';

  return (Array.isArray(items) ? items : [])
    .filter((item) => item && String(item.cusip || '').trim())
    .map((item) => {
      const isSourceBond =
        item.orderId !== null &&
        item.orderId !== undefined &&
        String(item.orderId) === String(sourceOrderId);

      const side = String(item.side || '').trim().toUpperCase();

      return createEmptyRow({
        // Upper-cased on the way in, the same normalising buildListRfqPayload applies before
        // sending tag 48 and the grid's own identifier cell applies as the trader types.
        cusip: String(item.cusip).trim().toUpperCase(),
        side: side === ORDER_SIDES.SELL ? ORDER_SIDES.SELL : ORDER_SIDES.BUY,
        qty: item.quantity === null || item.quantity === undefined ? '' : `${item.quantity}`,
        price: item.price === null || item.price === undefined ? '' : `${item.price}`,
        // Carried for the read-only view of an existing list, which shows a Status column.
        // Harmless on a re-initiate: nothing reads it while the grid is editable, and the
        // payload does not send it.
        rfqStatus: item.status ?? null,
        // The bond's own settings win; the blotter row only fills gaps, and only for the bond
        // it actually describes.
        ...(!isMarketAxess && isSourceBond ? rowSettings : {}),
        ...(!isMarketAxess ? buildPrefillRowSettingsFromItem(item) : {
          priceType: item.priceType || '',
          strategyType: item.strategyType || '',
          mktxTargetLevel: item.mktxTargetLevel ?? null,
          // A new enquiry uses createEmptyRow's fresh defaults. Viewing keeps saved times.
          ...(!reinitiate ? {
            effectiveTime: item.effectiveTime || null,
            expiryTime: item.expiryTime || null,
          } : {}),
          rfqId: item.rfqId ?? null,
          strategyParameters: item.strategyParameters || {},
          orderDealers: dealersPerBond ? savedDealerSet(item.venue) : [],
        }),
      });
    });
};
